import { date_to_datetime_local } from '@frontend/shared/src/utils/date_to_datetime_local';
import { e } from '@frontend/shared/src/utils/e';
import { TIME } from '@shared/src/utils/time';
import { WallOfShame } from '@frontend/wall-of-shame/src/services/WallOfShameService';
import type { WallOfShameUser } from '../wall-of-shame-user';

import './wall-of-shame-dialog-user-settings.css';

const ATTR = 'wall-of-shame-dialog-user-settings';

export class WallOfShameDialogUserSettings extends HTMLDialogElement {
  static from_user = (user: WallOfShameUser) => {
    const element = new WallOfShameDialogUserSettings();

    element.open = true;

    element.parent = user;
    element.user_id = user.user_id;
    element.name = user.name;
    element.reason = user.reason;
    element.unban_ts = user.unban_ts;
    element.banned_since = user.banned_since;

    return element;
  };

  static from_new = () => {
    const element = new WallOfShameDialogUserSettings();

    element.open = true;

    element.user_id = +Math.random().toString().slice(2);
    element.name = '';
    element.reason = '';
    element.unban_ts = Date.now();
    element.banned_since = Date.now();

    return element;
  };

  #remove_element!: HTMLElement;
  #save_button_element!: HTMLButtonElement;

  #parent: WallOfShameUser | null = null;
  #user_id: number = 0;
  #name: string = '';
  #reason: string = '';
  #unban_ts: number = 0;
  #banned_since_ts: number = 0;

  #name_input!: HTMLInputElement;
  #reason_textarea!: HTMLTextAreaElement;
  #unban_after_input!: HTMLInputElement;
  #banned_since_input!: HTMLInputElement;

  constructor() {
    super();

    this.setAttribute('is', ATTR);
    this.classList.add('no-scrollbar');
  }

  get parent() {
    return this.#parent;
  }
  set parent(value) {
    this.#parent = value;
  }

  get user_id() {
    return this.#user_id;
  }
  set user_id(value) {
    this.#user_id = value;
  }

  get name() {
    return this.#name;
  }
  set name(value) {
    this.#name = value;

    if (this.#name_input) {
      this.#name_input.value = this.#name;
    }
  }

  get reason() {
    return this.#reason;
  }
  set reason(value) {
    this.#reason = value;

    if (this.#reason_textarea) {
      this.#reason_textarea.value = this.#reason;
    }
  }

  get unban_ts() {
    return this.#unban_ts;
  }
  set unban_ts(value) {
    this.#unban_ts = value;

    if (this.#unban_after_input) {
      this.#unban_after_input.value = date_to_datetime_local(new Date(this.#unban_ts));
    }
  }

  get banned_since() {
    return this.#banned_since_ts;
  }
  set banned_since(value) {
    this.#banned_since_ts = value;

    if (this.#banned_since_input) {
      this.#banned_since_input.value = date_to_datetime_local(new Date(this.#banned_since_ts));
    }
  }

  connectedCallback() {
    this.#remove_element =
      this.querySelector<HTMLButtonElement>('.close') ??
      e.button({ className: 'close', onclick: this.#handle_close }, [e.icon('cross', 30)]);

    this.#name_input =
      this.querySelector<HTMLInputElement>('.name') ?? e.input({ className: 'name' });

    this.#reason_textarea =
      this.querySelector<HTMLTextAreaElement>('.reason') ?? e.textarea({ className: 'reason' });

    this.#unban_after_input =
      this.querySelector<HTMLInputElement>('.unban_after') ??
      e.input({ className: 'unban_after', type: 'datetime-local' });

    this.#banned_since_input =
      this.querySelector<HTMLInputElement>('.banned_since') ??
      e.input({ className: 'banned_since', type: 'datetime-local' });

    this.name = this.#name;
    this.reason = this.#reason;
    this.unban_ts = this.#unban_ts;
    this.banned_since = this.#banned_since_ts;

    this.#name_input.addEventListener('input', () => {
      this.#name = this.#name_input.value;

      this.#handle_inputs_change();
    });
    this.#reason_textarea.addEventListener('input', () => {
      this.#reason = this.#reason_textarea.value;

      this.#handle_inputs_change();
    });
    this.#unban_after_input.addEventListener('input', () => {
      this.#unban_ts = +new Date(this.#unban_after_input.value);

      this.#handle_inputs_change();
    });
    this.#banned_since_input.addEventListener('input', () => {
      this.#banned_since_ts = +new Date(this.#banned_since_input.value);

      this.#handle_inputs_change();
    });

    this.#save_button_element = e.button(
      { className: 'dialog-button save', onclick: this.#handle_save, disabled: true },
      ['Сохранить']
    );

    this.append(
      this.#remove_element,
      e.div({ className: 'block name', data: { title: 'Имя Пользователя' } }, [
        e.div({ className: 'content' }, [this.#name_input]),
      ]),
      e.div({ className: 'block reason', data: { title: 'Причина' } }, [
        e.div({ className: 'content' }, [this.#reason_textarea]),
      ]),
      e.div({ className: 'block unban_after', data: { title: 'Дата разбана' } }, [
        e.div({ className: 'content' }, [
          this.#unban_after_input,
          e.button({ className: 'dialog-button', onclick: this.#handle_minus_day }, ['-1  День']),
          e.button({ className: 'dialog-button', onclick: this.#handle_plus_day }, ['+1  День']),
        ]),
      ]),
      e.div({ className: 'block banned_since', data: { title: 'Дата бана' } }, [
        e.div({ className: 'content' }, [this.#banned_since_input]),
      ]),
      e.div({ className: 'block banned_since' }, [
        e.div(
          { className: 'content' },
          this.parent
            ? [
                e.button({ className: 'dialog-button delete', onclick: this.#handle_delete }, [
                  'Удалить',
                ]),
                this.#save_button_element,
              ]
            : [this.#save_button_element]
        ),
      ])
    );
  }

  #can_be_saved = () => {
    if (this.parent) {
      return (
        this.parent.name !== this.name ||
        this.parent.reason !== this.reason ||
        this.parent.unban_ts !== this.unban_ts ||
        this.parent.banned_since !== this.banned_since
      );
    } else {
      const now = Date.now();

      return (
        this.name !== '' || this.reason !== '' || this.unban_ts > now || this.banned_since > now
      );
    }
  };

  #handle_inputs_change = () => {
    this.#save_button_element.disabled = !this.#can_be_saved();
  };

  #handle_minus_day = () => {
    this.unban_ts = +new Date(this.#unban_after_input.value) - TIME.DAY;

    this.#handle_inputs_change();
  };

  #handle_plus_day = () => {
    this.unban_ts = +new Date(this.#unban_after_input.value) + TIME.DAY;

    this.#handle_inputs_change();
  };

  #handle_delete = () => {
    if (confirm('Точно? Мб подождать пока опять забанят?')) {
      WallOfShame.delete(this.#user_id).then(is_deleted => {
        if (is_deleted) {
          this.#handle_close();

          location.reload();
        } else {
          alert('Ошибка');
        }
      });
    }
  };

  #handle_save = () => {
    const parent = this.parent;

    const payload = {
      name: this.name,
      reason: this.reason,
      unban_ts: this.unban_ts,
      banned_since: this.banned_since,
    };

    if (parent) {
      WallOfShame.save(this.user_id, payload).then(result => {
        if (result) {
          parent.name = result.user.name;
          parent.reason = result.user.reason;
          parent.unban_ts = result.user.unban_ts;
          parent.banned_since = result.user.banned_since;

          this.#handle_close();
        } else {
          alert('Ошибка');
        }
      });
    } else {
      WallOfShame.create(payload).then(result => {
        if (result) {
          location.reload();
        } else {
          alert('Ошибка');
        }
      });
    }
  };

  #handle_close = () => {
    this.remove();
  };
}

customElements.whenDefined(ATTR).then(() => console.log('[DEFINED]', ATTR));
customElements.define(ATTR, WallOfShameDialogUserSettings, { extends: 'dialog' });
